import React from 'react';
import {
  AgnesAutocomplete,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesIconButton,
  AgnesInput,
  AgnesInputFile,
  AgnesLabel,
  AgnesModal,
  AgnesPageHeader,
} from '@agnes/ui';
import { Download, Eye, FolderOpen, Plus, Trash2, X } from 'lucide-react';
import { useDocumentosStore } from '@/stores/useDocumentosStore';
import { useEmpresasStore } from '@/stores/useEmpresasStore';
import { EmptyState } from '@/components/ui/EmptyState';
import { confirm } from '@/stores/useConfirm';
import { notify } from '@/stores/useToast';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';
import { employerListMessage } from '@/utils/employerListMessage';
import { EmployerListRetryButton } from '@/components/EmployerListRetryButton';

type Item = any;

type Empresa = any;

const ACCEPT = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'zip'];
const MAX_SIZE = 500 * 1024 * 1024; // 500MB
const empCnpj = (e: Empresa) => e.cnpj ?? e.cnpjFilial ?? '';
const empLabel = (e: Empresa) => e.socialName || e.razaoSocial || e.company || empCnpj(e);
const dataBR = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

export default function PageDocumentos() {
  const lista = useDocumentosStore((s) => s.lista);
  const carregando = useDocumentosStore((s) => s.carregando);
  const empresas = useEmpresasStore((s) => s.lista);
  const carregandoEmpresas = useEmpresasStore((s) => s.carregando);
  const empresasCarregadas = useEmpresasStore((s) => s.carregado);
  const erroEmpresas = useEmpresasStore((s) => s.erro);

  const [nome, setNome] = React.useState('');
  const [obs, setObs] = React.useState('');
  const [emps, setEmps] = React.useState<Empresa[]>([]);
  const [emailAtual, setEmailAtual] = React.useState('');
  const [emails, setEmails] = React.useState<string[]>([]);
  const [file, setFile] = React.useState<File | null>(null);
  const [open, setOpen] = React.useState(false);
  const [obsView, setObsView] = React.useState<Item | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    useEmpresasStore.getState().buscar();
    useDocumentosStore.getState().buscar();
  }, []);

  const toggleEmp = (e: Empresa) => {
    setEmps((prev) =>
      prev.some((p) => empCnpj(p) === empCnpj(e))
        ? prev.filter((p) => empCnpj(p) !== empCnpj(e))
        : [...prev, e],
    );
  };

  const addEmail = () => {
    const e = emailAtual.trim();
    if (e && emails.length < 10 && !emails.includes(e)) {
      setEmails((s) => [...s, e]);
      setEmailAtual('');
    }
  };

  const podeSalvar = nome !== '' && emps.length > 0 && file !== null;

  const resetForm = () => {
    setNome('');
    setObs('');
    setEmps([]);
    setEmailAtual('');
    setEmails([]);
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  async function enviar() {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ACCEPT.includes(ext)) {
      notify.error('Tipo de arquivo não suportado.');
      return;
    }
    if (file.size > MAX_SIZE) {
      notify.error('Arquivo excede o tamanho máximo de 500MB.');
      return;
    }
    const cnpjs = emps.map(empCnpj).filter(Boolean);
    if (cnpjs.length === 0) return;
    const ok = await useDocumentosStore
      .getState()
      .enviar({ nome, observacao: obs || undefined, cnpjs, emails, file });
    if (ok) {
      resetForm();
      setOpen(false);
    }
  }

  const abrirModal = () => setOpen(true);
  const fecharModal = () => {
    resetForm();
    setOpen(false);
  };

  const columns = [
    { key: 'nome', header: 'Nome', width: 200, render: (r: Item) => r.nome },
    {
      key: 'empregador',
      header: 'Empregador',
      width: 220,
      render: (r: Item) => (r.cnpjs ?? []).join(', ') || '-',
    },
    { key: 'data', header: 'Data', width: 120, render: (r: Item) => dataBR(r.createdAt) },
  ];

  async function onExcluir(r: Item) {
    const ok = await confirm({
      title: 'Excluir documento',
      message: `Excluir <b>${r.nome}</b>?`,
      okLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (ok) await useDocumentosStore.getState().excluir(r.id);
  }

  return (
    <>
      <AgnesPageHeader
        icon={<FolderOpen />}
        title="Adicionar documentos"
        subtitle="Disponibilize arquivos ao cliente"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Arquivos' },
          { label: 'Documentos' },
        ]}
        actions={
          <AgnesButton
            variant="primary"
            startIcon={<Plus className="h-4 w-4" />}
            onClick={abrirModal}
          >
            Novo documento
          </AgnesButton>
        }
      />

      <AgnesModal
        open={open}
        title="Cadastrar novo documento"
        maxWidth="lg"
        onClose={fecharModal}
        footer={
          <div className="flex justify-end gap-3">
            <AgnesButton variant="text" onClick={fecharModal}>
              Cancelar
            </AgnesButton>
            <AgnesButton variant="primary" disabled={!podeSalvar} onClick={enviar}>
              Salvar
            </AgnesButton>
          </div>
        }
      >
        <div>
          <div className="flex flex-col gap-4">
            <div>
              <AgnesInput
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome do arquivo *"
                maxLength={150}
              />
              <p className="mt-1 text-xs text-agnes-text-muted">{nome.length} /150 caracteres</p>
            </div>
            <div>
              <AgnesInput
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                placeholder="Observação"
                maxLength={500}
              />
              <p className="mt-1 text-xs text-agnes-text-muted">{obs.length} /500 caracteres</p>
            </div>
            <div>
              <AgnesLabel required>Empregador</AgnesLabel>
              <AgnesAutocomplete
                options={empresas}
                value={emps}
                onOptionToggle={toggleEmp}
                getOptionLabel={(e: Empresa) => empLabel(e)}
                isOptionEqualToValue={(a: Empresa, b: Empresa) => empCnpj(a) === empCnpj(b)}
                placeholder="Selecione o empregador"
                noOptionsText={employerListMessage({
                  loading: carregandoEmpresas,
                  loaded: empresasCarregadas,
                  error: erroEmpresas,
                })}
              />
              <EmployerListRetryButton
                error={erroEmpresas}
                onRetry={() => void useEmpresasStore.getState().buscar()}
              />
            </div>
          </div>

          <p className="mt-4 text-sm text-agnes-text-muted">
            Tamanho máximo do arquivo é 500MB. Os tipos de arquivos suportados são XLS, DOC, ZIP e
            pdf
          </p>
          <div className="mt-2">
            <AgnesInputFile
              ref={fileRef}
              accept=".pdf,.xls,.xlsx,.doc,.docx,.zip,application/pdf"
              buttonLabel="Adicionar arquivo *"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && <p className="mt-1 text-xs text-agnes-text-muted">{file.name}</p>}
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="max-w-md flex-1">
                <AgnesInput
                  type="email"
                  value={emailAtual}
                  onChange={(e) => setEmailAtual(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEmail();
                    }
                  }}
                  placeholder="E-mail do destinatário"
                />
              </div>
              <AgnesIconButton
                aria-label="Adicionar destinatário"
                onClick={addEmail}
                disabled={emails.length >= 10}
              >
                <Plus className="h-4 w-4" />
              </AgnesIconButton>
            </div>
            <p className="mt-1 text-xs text-agnes-text-muted">{emails.length} /10 destinatários</p>
            {emails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {emails.map((e) => (
                  <span
                    key={e}
                    className="flex items-center gap-1 rounded-full bg-agnes-primary/10 px-3 py-1 text-sm text-agnes-primary"
                  >
                    {e}
                    <button
                      type="button"
                      aria-label="Remover"
                      onClick={() => setEmails((s) => s.filter((x) => x !== e))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </AgnesModal>

      <AgnesModal
        open={!!obsView}
        title="Observação"
        maxWidth="md"
        onClose={() => setObsView(null)}
      >
        <p className="whitespace-pre-wrap break-words text-sm">{obsView?.observacao}</p>
      </AgnesModal>

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          {lista.length === 0 ? (
            <EmptyState
              icon={<FolderOpen className="h-7 w-7" />}
              title={carregando ? 'Carregando documentos…' : 'Nenhum documento'}
              description={
                carregando
                  ? 'Aguarde…'
                  : 'Nenhum documento cadastrado ainda. Use o botão "Novo documento" para disponibilizar arquivos ao cliente.'
              }
            />
          ) : (
            <TableWrapper>
              <AgnesDataTable<Item>
                columns={columns}
                data={lista}
                rowKey={(r) => String(r.id)}
                actions={(r: Item) => (
                  <div className="flex items-center gap-1">
                    <AgnesIconButton
                      aria-label="Ver observação"
                      data-qa="documentos-acao-observacao"
                      size="sm"
                      disabled={!r.observacao}
                      title={r.observacao ? 'Ver observação' : 'Sem observação'}
                      onClick={() => setObsView(r)}
                    >
                      <Eye className="h-4 w-4" />
                    </AgnesIconButton>
                    <AgnesButton
                      variant="ghost"
                      size="sm"
                      title="Baixar"
                      data-qa="documentos-acao-download"
                      onClick={() => useDocumentosStore.getState().baixar(r.id)}
                    >
                      <Download className="h-4 w-4" />
                    </AgnesButton>
                    <AgnesButton
                      variant="ghost"
                      size="sm"
                      title="Excluir"
                      data-qa="documentos-acao-excluir"
                      onClick={() => onExcluir(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </AgnesButton>
                  </div>
                )}
              />
            </TableWrapper>
          )}
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
